import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { RuleInputDialogComponent } from 'libs/shared/ui-common/src/lib/components/rule-input/rule-input-dialog/rule-input-dialog.component';
import { FilterOptions, MedicalComponentSearchComponent, SearchResultDto } from 'shared/feature-medical-component-search';
import { PopoverRef, DialogService, IconDirective, TooltipDirective } from 'shared/ui-common';

import {
  ComponentType,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartNodeType,
  FlowchartStartNodeDto,
  POPOVER_DATA,
} from '@treetop/shared/model';

export type MedicalComponentSearchPopoverData = {
  definition?: FormControl<string>;
  conditionFilterActive?: boolean;
  findingFilterActive?: boolean;
  activityFilterActive?: boolean;
  observationFilterActive?: boolean;
  enableMultiSearch?: boolean;
  element?: FlowchartNodeType;
  allowProposedComponents?: boolean;
  isFilterSearch?: boolean;
  isDecisionNode?: boolean;
  taxonomyId?: string;
};

@Component({
  selector: 'medical-component-search-popover',
  standalone: true,
  imports: [MedicalComponentSearchComponent, MatIconModule, TranslocoPipe, TooltipDirective, MatFormFieldModule, IconDirective],
  templateUrl: './medical-component-search-popover.component.html',
  styleUrls: ['./medical-component-search-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MedicalComponentSearchPopoverComponent implements OnInit {
  public data = inject<MedicalComponentSearchPopoverData>(POPOVER_DATA);
  public popoverRef = inject(PopoverRef);
  private dialogService = inject(DialogService);

  protected searchOptions: FilterOptions = {
    possibleComponentTypes: [],
    taxonomyId: this.data.taxonomyId,
    useProposedComponent: this.data.allowProposedComponents !== false,
  };

  public ngOnInit() {
    const possibleComponentTypes = [];

    if (this.data.activityFilterActive) {
      possibleComponentTypes.push(ComponentType.ActivityDefinition);
    }
    if (this.data.conditionFilterActive) {
      possibleComponentTypes.push(ComponentType.ConditionDefinition);
    }
    if (this.data.findingFilterActive) {
      possibleComponentTypes.push(ComponentType.FindingDefinition);
    }
    if (this.data.observationFilterActive) {
      possibleComponentTypes.push(ComponentType.ObservationDefinition);
    }

    this.searchOptions.possibleComponentTypes = possibleComponentTypes;
    this.searchOptions.usableComponents = true;
  }

  protected selectedComponent(searchResultDto: SearchResultDto) {
    if (searchResultDto.componentDto) {
      this.popoverRef.close(new FormControl(searchResultDto.componentDto));
    } else if (searchResultDto.proposedComponentDto) {
      this.popoverRef.close(new FormControl(searchResultDto.proposedComponentDto));
    } else if (searchResultDto.newComponentText) {
      this.popoverRef.close(searchResultDto.newComponentText);
    }
  }

  protected openRuleDialog($event: MouseEvent) {
    $event.stopPropagation();

    if (!this.data.element) {
      return;
    }

    let rule = '';
    let prettyfiedRule = '';
    if (this.data.element.elementType === FlowchartElementType.FlowchartDecisionNode) {
      const flowchartDecisionNode = this.data.element as FlowchartDecisionNodeDto;
      rule = flowchartDecisionNode.rule;
      prettyfiedRule = flowchartDecisionNode.rule;
    } else if (this.data.element.elementType === FlowchartElementType.FlowchartStartNode) {
      const flowchartStartNode = this.data.element as FlowchartStartNodeDto;
      rule = flowchartStartNode.rule;
      prettyfiedRule = flowchartStartNode.rule;
    }

    const ruleDialog = this.dialogService.open(RuleInputDialogComponent, {
      width: '60%',
      data: {
        label: 'Definition',
        rule,
        prettyfiedRule,
      },
    });

    ruleDialog.afterClosed().subscribe(({ rule, prettified }) => {
      if (rule && prettified) {
        if (this.data.element.elementType === FlowchartElementType.FlowchartDecisionNode) {
          const ruleTitle = new DOMParser().parseFromString(prettified, 'text/html').documentElement.textContent;
          const flowchartDecisionNode = this.data.element as FlowchartDecisionNodeDto;
          flowchartDecisionNode.rule = rule;
          flowchartDecisionNode.rulePrettyfied = prettified;
          flowchartDecisionNode.ruleTitle = ruleTitle;
          flowchartDecisionNode.guidelineCriteria = null;
          flowchartDecisionNode.guidelineCriteriaId = null;
          flowchartDecisionNode.guidelineObservation = null;
          flowchartDecisionNode.guidelineObservationId = null;
        } else if (this.data.element.elementType === FlowchartElementType.FlowchartStartNode) {
          const flowchartStartNode = this.data.element as FlowchartStartNodeDto;
          flowchartStartNode.rule = rule;
          flowchartStartNode.rulePrettyfied = prettified;
        }
        const formControl = new FormControl('');
        formControl.patchValue(this.data.element as string);
        this.popoverRef.close(formControl);
      }
    });
  }

  protected readonly FlowchartElementType = FlowchartElementType;
}
